import React, { useState, useCallback, useRef, useEffect } from 'react';
import InsufficientCreditsModal from '../InsufficientCreditsModal';
import { auth } from '../../firebase/firebase';
import { compressPhoto } from '../../utils/imageCompress';
import { saveOrShareImage, isIOSDevice } from '../../utils/screenshot';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n';
import { legalCopy } from '../../i18n/legal';
import {
  ROOM_TYPES, DESIGN_STYLES, COLOR_PALETTES, ROOM_STATES, SURFACES, SURFACE_IDS,
  buildRealStagingPrompt,
} from './realStagingPrompt';
import './RealStaging.css';

// ─── REAL ROOM AI VIRTUAL STAGING ────────────────────────────────────────────
//
// Photograph of a real room in, furnished photograph of the same room out.
//
// Reuses the EXISTING generateImageWithFlux function unchanged. That endpoint
// already takes { prompt, inputImage } and nothing about it is specific to the
// 3D views, so this needs no backend change and no redeploy — which also means
// this feature cannot break the AI render that is already working in production.

const FN_BASE =
  `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
const FLUX_ENDPOINT = `${FN_BASE}/generateImageWithFlux`;

// The upload is capped at 1024px on its long side. Kontext works at around a
// megapixel, so a 12MP phone photo is entirely wasted bandwidth — and the
// request body is JSON, where every byte costs a third more again in base64.
const UPLOAD_MAX_PX = 1024;

// The room types a reader may pick — every real one, and NOT `auto`. See the
// note on the `roomType` state below: the only moment this list is shown is
// the empty-room case, which is precisely when deferring to the model fails.
const PICKABLE_ROOMS = ROOM_TYPES.filter((r) => r.furniture);

export default function RealStagingModal({ open, onClose }) {
  const { t, lang } = useT();

  const [photo, setPhoto] = useState(null);          // { fullUrl, width, height, bytes }
  // ── WHO DECIDES WHAT KIND OF ROOM THIS IS ─────────────────────────────────
  // It depends entirely on whether the photo shows anything.
  //
  //   · a FURNISHED room answers the question itself — the bed, the hob, the
  //     bath are right there in the picture, and the model can see them better
  //     than a dropdown can guess. So the choice is not offered at all and
  //     `auto` is used, which asks the model to work it out.
  //   · an EMPTY room answers nothing. Four bare walls are a bedroom, an office
  //     or a dining room with equal plausibility, and `auto` there is a coin
  //     toss. So the reader is asked — and `auto` is not among the options,
  //     because deferring is exactly what does not work in this case.
  //
  // The default is the first REAL type rather than `auto`, since the only time
  // this control is visible is the case where `auto` is the wrong answer.
  const [roomType, setRoomType] = useState(PICKABLE_ROOMS[0].id);
  const [designStyle, setDesignStyle] = useState(DESIGN_STYLES[0].id);
  const [colorPalette, setColorPalette] = useState(COLOR_PALETTES[0].id);
  const [roomState, setRoomState] = useState(ROOM_STATES[0].id);
  // ── WHAT MAY BE REDESIGNED ────────────────────────────────────────────────
  // ALL OF THEM BY DEFAULT. This replaces a "renovation state" dropdown that
  // defaulted to "finished", which froze the floor, the walls and the ceiling —
  // so the common case was a reader asking for a redesign and being given the
  // same room with new furniture in it. Someone protecting a finish they like
  // can untick it; nobody should have to tick four boxes to get a redesign
  // from a redesign tool.
  const [surfaces, setSurfaces] = useState(() => new Set(SURFACE_IDS));
  const toggleSurface = (id) => setSurfaces((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── TWO COLOURS OF THEIR OWN ──────────────────────────────────────────────
  // Off by default and behind a tick, because `<input type="color">` always
  // HAS a value — it opens on black — so there is no "unset" state to read.
  // Without the tick, every generation would silently be told to build the
  // scheme from black and black.
  const [useOwnColors, setUseOwnColors] = useState(false);
  const [colorA, setColorA] = useState('#2E4A3D');
  const [colorB, setColorB] = useState('#C9A227');

  const [customNotes, setCustomNotes] = useState('');

  // Declared HERE, above `generate`, because that callback closes over it —
  // below the early return would be too late.
  //
  // FURNISHED SENDS `auto` whatever the hidden control holds. The room-type
  // dropdown is unmounted in that case but its state survives, and a value the
  // reader cannot see must never reach the prompt.
  const roomIsEmpty = roomState === 'empty';
  const effectiveRoomType = roomIsEmpty ? roomType : 'auto';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Set only from the server reply, so the figures shown are the ones that
  // actually refused the request. null = no dialog.
  const [noCredits, setNoCredits] = useState(null);
  const [result, setResult] = useState(null);        // data URL of the staged room
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  // Reset on close, so reopening never shows the previous room's result.
  useEffect(() => {
    if (!open) {
      setPhoto(null); setResult(null); setError(null);
      setBusy(false); setCustomNotes('');
    }
  }, [open]);

  const takeFile = useCallback(async (file) => {
    setError(null);
    setResult(null);
    if (!file) return;
    if (!/^image\//.test(file.type)) { setError(t('rs_err_not_image')); return; }
    try {
      // Compressed in the browser before it goes anywhere — see UPLOAD_MAX_PX.
      const out = await compressPhoto(file, { fullMaxPx: UPLOAD_MAX_PX, thumbMaxPx: 320 });
      setPhoto(out);
    } catch {
      setError(t('rs_err_read'));
    }
  }, [t]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    takeFile(e.dataTransfer?.files?.[0]);
  }, [takeFile]);

  const generate = useCallback(async () => {
    if (!photo || busy) return;
    setError(null);
    setBusy(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error(t('dl_ai_signin'));
      const idToken = await user.getIdToken();

      const res = await fetch(FLUX_ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: buildRealStagingPrompt({
            roomType: effectiveRoomType,
            designStyle, colorPalette, roomState, customNotes,
            surfaces: [...surfaces],
            customColors: useOwnColors ? [colorA, colorB] : null,
          }),
          inputImage: photo.fullUrl,
          // Selects the staging model server-side. A NAME, not a model id —
          // the backend maps it, so a client can never pick what runs on the
          // project's Replicate account. Restyling a real photograph is a much
          // harder instruction to hold to than restyling a 3D render, which is
          // why this flow gets the higher tier; the 3D path sends no mode and
          // stays exactly where it was. See generateImageFlux.js.
          mode: 'staging',
        }),
      });

      const payload = await res.json().catch(() => ({}));
      // ── NOT AN ERROR, A PRICE ───────────────────────────────────────────
      // The server refused this for want of credits: no provider was called,
      // nothing failed, and there is a specific thing the reader can do. It
      // must not reach the generic red banner, which would say the AI broke.
      //
      // A plain return rather than a throw, so the finally below still resets
      // the button — the reader can act on the dialog and try again.
      if (!res.ok && payload.code === 'INSUFFICIENT_CREDITS') {
        setNoCredits({
          current: payload.currentCredits,
          required: payload.requiredCredits,
        });
        return;
      }
      if (!res.ok) throw new Error(payload.error || t('dl_ai_failed'));

      // Prefer the inlined base64 — a data URL cannot expire or be blocked by
      // CORS, unlike the short-lived URL the provider hands back.
      const url = payload.dataUrl
        || (payload.imageBase64 && `data:${payload.mimeType || 'image/png'};base64,${payload.imageBase64}`)
        || payload.imageUrl;
      if (!url) throw new Error(t('dl_ai_failed'));
      setResult(url);
    } catch (err) {
      setError(err?.message || t('dl_ai_failed'));
    } finally {
      setBusy(false);
    }
  }, [photo, busy, effectiveRoomType, designStyle, colorPalette, roomState, surfaces,
      useOwnColors, colorA, colorB,
      customNotes, t]);

  const save = useCallback(() => {
    if (result) saveOrShareImage(result, 'tunify-staged-room.png', t('rs_title'));
  }, [result, t]);

  // (`roomIsEmpty` and `effectiveRoomType` are declared above `generate`,
  // which closes over them.)

  if (!open) return null;

  const select = (label, value, onChange, options, hint, wide) => (
    <label className={`rs-field${wide ? ' rs-field--wide' : ''}`}>
      <span className="rs-label">{label}</span>
      <select
        className="rs-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{t(`rs_opt_${o.id}`)}</option>
        ))}
      </select>
      {hint && <span className="rs-hint">{hint}</span>}
    </label>
  );

  return (
    <div
      className="rs-overlay"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="rs-modal" role="dialog" aria-modal="true" aria-labelledby="rs-title">
        <div className="rs-head">
          <div>
            <h3 className="rs-title" id="rs-title">{t('rs_title')}</h3>
            <p className="rs-sub">{t('rs_sub')}</p>
          </div>
          {/* `cancel` rather than a new `close` key — the app has no `close`
              string, and inventing one for a single icon button is not worth
              three more translations. */}
          <button className="rs-close" onClick={onClose} disabled={busy} aria-label={t('cancel')}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="rs-body">
          {/* ── The photo ── */}
          {!result && (
            <div
              className={`rs-drop${dragging ? ' rs-drop--over' : ''}${photo ? ' rs-drop--has' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => !busy && fileRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => takeFile(e.target.files?.[0])}
              />
              {photo ? (
                <>
                  <img className="rs-preview" src={photo.fullUrl} alt={t('rs_your_photo')} />
                  <span className="rs-meta">
                    {photo.width}×{photo.height} · {Math.round(photo.bytes / 1024)} KB · {t('rs_replace')}
                  </span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span className="rs-drop-title">{t('rs_upload')}</span>
                  <span className="rs-drop-hint">{t('rs_upload_hint')}</span>
                </>
              )}
            </div>
          )}

          {/* ── The result ── */}
          {result && (
            <div className="rs-result">
              <img className="rs-preview" src={result} alt={t('rs_title')} />
            </div>
          )}

          {/* ── The choices ── */}
          {!result && (
            <div className="rs-grid">
              {/* ── FIRST, AND IT DECIDES WHAT COMES NEXT ──
                  Whether the room is empty is the one question the photograph
                  cannot be trusted to answer for itself, and the answer governs
                  whether the reader is asked what kind of room this is. So it
                  leads. */}
              {select(t('rs_state'), roomState, setRoomState, ROOM_STATES)}

              {/* ONLY FOR AN EMPTY ROOM, and with no `auto` option — see the
                  note on the roomType state. A furnished photo answers the
                  question itself, so the control is absent rather than
                  disabled: there is nothing for the reader to decide. */}
              {roomIsEmpty
                && select(t('rs_room_type'), roomType, setRoomType, PICKABLE_ROOMS)}

              {select(t('rs_style'), designStyle, setDesignStyle, DESIGN_STYLES)}
              {/* The palette select is DISABLED, not hidden, while a custom
                  pair is in use: hiding it would leave the reader wondering
                  where their palette went, where greying it says plainly that
                  the two swatches have taken over. */}
              <label className={`rs-field${useOwnColors ? ' rs-field--off' : ''}`}>
                <span className="rs-label">{t('rs_palette')}</span>
                <select
                  className="rs-select"
                  value={colorPalette}
                  onChange={(e) => setColorPalette(e.target.value)}
                  disabled={busy || useOwnColors}
                >
                  {COLOR_PALETTES.map((o) => (
                    <option key={o.id} value={o.id}>{t(`rs_opt_${o.id}`)}</option>
                  ))}
                </select>
              </label>

              {/* ── TWO COLOURS OF YOUR OWN ──
                  REPLACES the palette rather than adding to it — "warm
                  neutrals, and also these two greens" is two palettes, and
                  mixing palettes is what every other line in the prompt is
                  trying to prevent. */}
              <div className="rs-field rs-colors">
                <label className="rs-check rs-check--own">
                  <input
                    type="checkbox"
                    checked={useOwnColors}
                    onChange={(e) => setUseOwnColors(e.target.checked)}
                    disabled={busy}
                  />
                  <span>{t('rs_own_colors')}</span>
                </label>
                {useOwnColors && (
                  <div className="rs-colors-row">
                    <label className="rs-swatch">
                      <input type="color" value={colorA} disabled={busy}
                        onChange={(e) => setColorA(e.target.value)} />
                      <span>{t('rs_color_main')}</span>
                    </label>
                    <label className="rs-swatch">
                      <input type="color" value={colorB} disabled={busy}
                        onChange={(e) => setColorB(e.target.value)} />
                      <span>{t('rs_color_accent')}</span>
                    </label>
                  </div>
                )}
              </div>

              {/* ── WHAT MAY BE REDESIGNED ──
                  Five boxes, replacing a two-way "renovation state" that could
                  only freeze every surface or redo every surface — and could
                  say nothing at all about doors and windows, which were frozen
                  by the geometry rule whether you wanted them or not. See
                  SURFACES. */}
              <fieldset className="rs-field rs-field--wide rs-checks">
                <legend className="rs-label">{t('rs_surfaces')}</legend>
                <div className="rs-checks-row">
                  {SURFACES.map((s) => (
                    <label key={s.id} className="rs-check">
                      <input
                        type="checkbox"
                        checked={surfaces.has(s.id)}
                        onChange={() => toggleSurface(s.id)}
                        disabled={busy}
                      />
                      <span>{t(`rs_surf_${s.id}`)}</span>
                    </label>
                  ))}
                </div>
                {/* The one thing a reader is right to worry about, answered
                    before they have to ask it. */}
                <span className="rs-hint">{t('rs_surfaces_hint')}</span>
              </fieldset>

              <label className="rs-field rs-field--wide">
                <span className="rs-label">{t('rs_notes')}</span>
                <textarea
                  className="rs-textarea"
                  rows={2}
                  maxLength={300}
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                  placeholder={t('rs_notes_ph')}
                  disabled={busy}
                />
                {/* This note is dropped verbatim into an otherwise English
                    prompt, and the model handles English far better than
                    Armenian or Russian. The hint is translated (so it is read),
                    the example is not (so it is copied). */}
                <span className="rs-hint">{t('rs_notes_hint')}</span>
              </label>
            </div>
          )}

          {error && <p className="rs-error" role="alert">⊗ {error}</p>}

          {/* The architecture is frozen by the prompt, but the model still
              reinterprets surfaces, so this is set out plainly rather than
              discovered after a disappointing render. */}
          {!result && <p className="rs-note">{t('rs_disclaimer')}</p>}

          {/* ── AND AGAIN ONCE THERE IS A RENDER ───────────────────────────
              The note above is shown while someone is deciding whether to
              generate. This one is shown when they are looking at a finished
              image and about to download it, act on it, or send it to a
              builder — which is the moment the caveat actually costs
              something to ignore.

              Deliberately a DIFFERENT point, not a repeat: that one is about
              finishes drifting from the photo, this one is that the same
              settings give different results and that no measurement should
              come from a render. Full version on /about. */}
          {result && (
            <p className="rs-note rs-note--after">
              {legalCopy(lang).ai_short}
              {' '}
              <Link to="/about" className="rs-note-link" target="_blank" rel="noreferrer">
                {t('nav_about')}
              </Link>
            </p>
          )}
        </div>

        <div className="rs-foot">
          {result ? (
            <>
              <button className="rs-btn rs-btn--outline" onClick={() => setResult(null)} >
                {t('rs_again')}
              </button>
              <button className="rs-btn rs-btn--primary" onClick={save}>
                {isIOSDevice() ? t('rs_open_image') : t('rs_download')}
              </button>
            </>
          ) : (
            <>
              <button className="rs-btn rs-btn--outline" onClick={onClose} disabled={busy}>
                {t('cancel')}
              </button>
              <button
                className="rs-btn rs-btn--primary"
                onClick={generate}
                disabled={!photo || busy}
              >
                {busy ? <><span className="rs-spinner" />{t('rs_working')}</> : t('rs_generate')}
              </button>
            </>
          )}
        </div>
      </div>
      {/* Position here is cosmetic: the dialog portals to <body>, so it is
          never clipped by this panel wherever it sits in the tree. */}
      <InsufficientCreditsModal
        open={Boolean(noCredits)}
        currentCredits={noCredits?.current}
        requiredCredits={noCredits?.required}
        onClose={() => setNoCredits(null)}
      />
    </div>
  );
}
